/**
 * DashboardRouter — picks a role-specific dashboard.
 *
 * Routing matrix:
 *   supervisor / contractor_admin / super_admin  → SupervisorDashboard
 *   maintenance                                  → MaintenanceDashboard
 *   owner_approver                               → FeeRequestsPage (#76)
 *   cleaner / client_view                        → CleanerDashboard
 *
 * `contractor_admin` is the tenant owner. They need the "+ New task"
 * button + pending-inspection queue + corrections link — all on
 * SupervisorDashboard. Routing them to CleanerDashboard renders an
 * empty "my assigned tasks" list because admins aren't assigned tasks,
 * which surfaced 2026-05-10 as a blank-page report from the Director
 * on first sign-in. `client_view` stays on the cleaner list view
 * (read-only by design). `owner_approver` (the client-side approver
 * of additional-fee requests) lands directly on the decision queue.
 */

import { type ReactNode }        from 'react'
import { useRole }               from '@/hooks/useAuth'
import { CleanerDashboard }      from './CleanerDashboard'
import { SupervisorDashboard }   from './SupervisorDashboard'
import { MaintenanceDashboard }  from './MaintenanceDashboard'
import { FeeRequestsPage }       from '@/pages/admin/FeeRequestsPage'

export function DashboardRouter(): ReactNode {
  const role = useRole()
  switch (role) {
    case 'supervisor':
    case 'contractor_admin':
    case 'super_admin':
      return <SupervisorDashboard />
    case 'maintenance':
      return <MaintenanceDashboard />
    case 'owner_approver':
      return <FeeRequestsPage />
    case 'cleaner':
    case 'client_view':
      return <CleanerDashboard />
    default:
      return <CleanerDashboard />
  }
}
